import React, { useEffect, useState, useRef } from "react";
import { ALMObject, fetchLearningObjects } from "../../utils/myLearning";
import styles from "./MyLearning.module.css";
import { getAccessToken, getALMConfig } from "../../utils/global";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";

interface LearningTilesProps {
    filterType: "completed" | "inprogress" | "recommendation";
}

const MyLearning = (props: any) => {
    const myLearningCmp = useRef<HTMLDivElement | null>(null);
    const [dataProps, setDataProps] = useState<any>(null);
    const [response, setResponse] = useState<any>(null); // Store full API response
    const [currentUrl, setCurrentUrl] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [prevUrls, setPrevUrls] = useState<string[]>([]); // Track previous self links
    const [width, setWidth] = useState<number>(window.innerWidth);
    const [endLocked, setEndLocked] = useState(false);
    const [beginLocked, setBeginLocked] = useState(false);
    const [isNoBorder, setIsNoBorder] = useState(false);


    const baseApiUrl = getALMConfig().primeApiURL;

    function handleWindowSizeChange() {
        setWidth(window.innerWidth);
    }
    useEffect(() => {
        window.addEventListener("resize", handleWindowSizeChange);
        return () => {
            window.removeEventListener("resize", handleWindowSizeChange);
        };
    }, []);

    const isMobile = width <= 450;

    useEffect(() => {
        if (myLearningCmp.current) {
            const parentEl = myLearningCmp.current.parentElement;
            let border = false;

            if (parentEl) {
                if (parentEl.classList.contains("cmp-my-learning-no-boarder")) {
                    setIsNoBorder(true);
                    border = true;
                }

                if (parentEl?.dataset) {
                    const extractedProps = {
                        filterType: parentEl.getAttribute("data-filtertype"),
                        title: parentEl.getAttribute("data-cmptitle"),
                        path: parentEl.getAttribute("data-detailpagepath"),
                        itemCount: parentEl.getAttribute("data-itemcount") || (border ? "4" : "3"),
                        continue: parentEl.getAttribute("data-continuebuttontext") || "continue",
                        revisit: parentEl.getAttribute("data-revisitbuttontext") || "revisit",
                        noResultText: parentEl.getAttribute("data-noResultText") || "No Result to display.",
                    };
                    setDataProps(extractedProps);
                }
            }
        }
    }, []);

    useEffect(() => {
        let url = "";
        if (dataProps?.filterType === "completed") {
            url = `${baseApiUrl}/enrollments?include=learningObject&page[limit]=${dataProps?.itemCount || (isNoBorder ? "4" : "3")
                }&filter.loTypes=course%2ClearningProgram%2CjobAid%2Ccertification&filter.completed=true&includeHierarchicalEnrollments=false&sort=-dateEnrolled`;
        }
        if (dataProps?.filterType === "enrolled") {
            url = `${baseApiUrl}/enrollments?include=learningObject&page[limit]=${dataProps?.itemCount || (isNoBorder ? "4" : "3")
                }&filter.loTypes=course%2ClearningProgram%2CjobAid%2Ccertification&filter.completed=false&includeHierarchicalEnrollments=false&sort=-dateEnrolled`;
        }
        if (dataProps?.filterType === "recommended") {
            url = `${baseApiUrl}/recommendations?filter.loTypes=course%2ClearningProgram%2Ccertification%2CjobAid&include=learningObject.instances%2ClearningObject.skills.skillLevel.skill&page[limit]=${dataProps?.itemCount || (isNoBorder ? "4" : "3")
                }&useCache=true&filter.ignoreEnhancedLP=false&enforcedFields%5BlearningObject%5D=extensionOverrides&filter.recType=peer_group&page%5Blimit%5D=10&omitDeprecated=true&access_token=${getAccessToken()}`;
        }
        setCurrentUrl(url);
    }, [props.filterType, baseApiUrl, dataProps, isNoBorder]);

    useEffect(() => {
        if (!currentUrl) return;
        setLoading(true);
        fetchLearningObjects(currentUrl)
            .then((data) => {
                setResponse(data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [currentUrl]);

    const handleNext = () => {
        if (response?.links?.next) {
            // Push current self to prevUrls before going next
            if (response?.links?.self) {
                setPrevUrls((prev) => [...prev, response.links.self]);
            }
            setCurrentUrl(response.links.next);
        }
    };

    const handlePrev = () => {
        if (prevUrls.length > 0) {
            const lastUrl = prevUrls[prevUrls.length - 1];
            setPrevUrls((prev) => prev.slice(0, -1));
            setCurrentUrl(lastUrl);
        }
    };

    const items = response?.data || [];

    return (
        <div className={`${styles.pageContainer} ${isNoBorder && styles.noBorder}`} ref={myLearningCmp}>
            <div className={styles.sectionTitle}>
                <h4>{props.title || dataProps?.title || "RECOMMENDED LEARNING"}</h4>
                {items.length === 0 ? '' : !isMobile && (
                    <div className={styles.carouselNav}>
                        <button
                            className={styles.navBtn}
                            aria-label="Previous"
                            onClick={handlePrev}
                            disabled={prevUrls.length === 0}
                        />
                        <button
                            className={styles.navBtn}
                            aria-label="Next"
                            onClick={handleNext}
                            disabled={!response?.links?.next}
                        />
                    </div>
                )}
            </div>

            <div className={styles.learningContainer}>
                <Swiper
                    key={currentUrl}
                    spaceBetween={30}
                    slidesPerView={isMobile ? 1 : (isNoBorder ? 4 : 3)}
                    modules={[Pagination]}
                    onSlideChange={(swiper: any) => {
                        if (!swiper.isEnd) setEndLocked(false);
                        if (!swiper.isBeginning) setBeginLocked(false);
                    }}
                    onReachEnd={() => {
                        if (!endLocked) {
                            setEndLocked(true);
                            handleNext();
                        }
                    }}
                    onReachBeginning={() => {
                        if (!beginLocked) {
                            setBeginLocked(true);
                            handlePrev();
                        }
                    }}
                    pagination={{
                        type: "bullets",
                        clickable: true
                    }}
                >
                    {items && items.length > 0 ? (
                        items.map((item: any, idx: number) => (
                            <SwiperSlide key={idx} className={styles.customSlide}>
                                <a href={`${dataProps.path}.html/trainingId/${item.course}/trainingInstanceId/${item.instance}`} className={styles.removeUnderline}>
                                    <div className={styles.learningCard}>
                                        {item.imageUrl || item.bannerUrl ? <img
                                            src={item.imageUrl || item.bannerUrl}
                                            alt={item.title}
                                            className={styles.cardImage}
                                        /> : <div className={styles.abbottImage}></div>}
                                        <div className={styles.cardContent}>
                                            <h4 className={styles.cardTitle}>{item.title}</h4>
                                            <p className={styles.cardDesc}>{item.description}</p>
                                            <div className={styles.bottomContainer}>
                                                <div className={styles.progressRow}>
                                                    <span className={styles.progressText}>
                                                        {item.progressPercent}% completed
                                                    </span>
                                                    <div className={styles.progressBarWrap}>
                                                        <div
                                                            className={styles.progressBar}
                                                            style={{ width: `${item.progressPercent}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                                {dataProps?.path && (
                                                    <div className={styles.cardFooter}>
                                                        <a
                                                            href={`${dataProps.path}.html/trainingId/${item.course}/trainingInstanceId/${item.instance}`}
                                                            className={styles.continueLink}
                                                        >
                                                            {dataProps?.filterType === "completed" ? dataProps?.revisit || "REVISIT" : dataProps?.continue || "CONTINUE"}
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            </SwiperSlide>
                        ))) : (

                        <div className={styles.noResult}>
                            {props.noResultText ||
                                dataProps?.noResultText ||
                                (dataProps?.filterType === "completed"
                                    ? "You haven't completed any courses yet. Browse courses to get started."
                                    : dataProps?.filterType === "recommended"
                                        ? "No recommended trainings available for you at the moment. Browse courses to get started."
                                        : dataProps?.filterType === "enrolled"
                                            ? "You haven't enrolled in any courses yet. Browse courses to get started."
                                            : "No Result to show. Browse courses to get started.")}
                        </div>

                    )
                    }

                    {loading && (
                        <SwiperSlide key="loading">
                            <div className={styles.learningCard}>
                                <div className={styles.cardContent}>
                                    <h4 className={styles.cardTitle}>Loading...</h4>
                                </div>
                            </div>
                        </SwiperSlide>
                    )}
                </Swiper>
            </div>
        </div>
    );
};

export default MyLearning;
